import { Component, h, Prop, State, forceUpdate, Host } from "@stencil/core";
import { TrainingItem } from "./stateful-trainings-interface";
import { i18nProvider } from "@trumpf/ux.i18n";

type SelectedEvent = {
    startDate: string;
    languages: string;
    duration: string;
    location: string;
    availability: string;
    detailsLink: string;
};

@Component({
    tag: "ux-iws-stateful-training-item",
    shadow: false,
})
export class UxIwsStatefulTrainingItem {
    /** each training item */
    @Prop() item: TrainingItem;
    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";
    /** namespace that's used for translations -> must be the same as the one of ux-iws-stateful-trainings */
    @Prop() translationsNamespace: string = "";

    @State() selectedEvent: SelectedEvent = {
        startDate: "",
        languages: "",
        duration: "",
        location: "",
        availability: "",
        detailsLink: "",
    };

    @State() selectedIndex: number = 0;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        if (this.language !== "") {
            i18nProvider.changeLanguage(this.language);
        }
    }

    private handleSelectChange = (event: CustomEvent<string>) => {
        const selectedValues = JSON.parse(event.detail);
        this.selectedEvent = selectedValues;
    };

    private handleSelectChangeIndex = (event: Event) => {
        const selectedElement = event.target as HTMLSelectElement;
        this.selectedIndex = selectedElement.selectedIndex;
    };

    private translateEventValue = (value: string | string[], key: string) => {
        const { t } = i18nProvider;

        if (Array.isArray(value)) {
            return value
                .map((v) => {
                    const translated = t(
                        `${this.translationsNamespace}:content.${key}.values.${v}`
                    );
                    return translated != null && translated !== ""
                        ? translated
                        : v;
                })
                .join(", ");
        } else {
            const translationKey = `${this.translationsNamespace}:content.${key}.values.${value}`;
            const translated = t(translationKey);
            return translated != null && translated !== "" ? translated : value;
        }
    };

    private renderEventInfo(
        event: Record<string, any>,
        selectedEvent: SelectedEvent,
        isOnlineEvent: boolean
    ) {
        const eventPairs = Object.entries(event);
        const { t } = i18nProvider;
        const filteredEvents = eventPairs
            .filter(([key]) => {
                if (isOnlineEvent) {
                    return (
                        key === "duration" ||
                        key === "languages" ||
                        key === "availability" ||
                        key === "detailsLink"
                    );
                } else {
                    return (
                        key === "startDate" ||
                        key === "languages" ||
                        key === "duration" ||
                        key === "location" ||
                        key === "availability" ||
                        key === "detailsLink"
                    );
                }
            })
            .map(([key, value]) => {
                const fieldLabel = t(
                    `${this.translationsNamespace}:content.${key}.label`
                );
                const translateLanguages = (languages) => {
                    return languages
                        .map((language) => {
                            const translated = t(
                                `${this.translationsNamespace}:filter.languages.values.${language}`
                            );
                            return translated != null && translated !== ""
                                ? translated
                                : language;
                        })
                        .join(", ");
                };
                return (
                    <div
                        key={key}
                        class={`${
                            key === "detailsLink"
                                ? "ux-u-ml-xl-auto"
                                : "ux-u-mr-xxs"
                        } ux-col-12 ux-col-lg-auto ux-u-mt-xxs`}
                    >
                        {key === "detailsLink" ? (
                            <a
                                key={key}
                                href={value}
                                class="ux-button ux-button--icon-only ux-button--white"
                            >
                                <ux-icon name="chevron-right"></ux-icon>
                            </a>
                        ) : (
                            <div>
                                <span class="ux-u-d-block ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                    {fieldLabel || key}
                                </span>
                                <span class="ux-u-d-block ux-text-normal">
                                    {key === "languages"
                                        ? translateLanguages(value)
                                        : selectedEvent[key] || value}
                                </span>
                            </div>
                        )}
                    </div>
                );
            });
        return filteredEvents;
    }

    render() {
        const { item, selectedEvent, selectedIndex } = this;
        const events = item.events || [];
        const isOnlineEvent = item.typeId === 1;
        const { t, language } = i18nProvider;

        if (events.length === 0) {
            return null;
        }

        return (
            <Host lang={language}>
                <div class="ux-u-shadow ux-u-mb-xs ux-u-px-xxs ux-u-px-md-xs ux-u-pt-xs ux-u-pb-xs">
                    <h4 class="ux-text-headline--medium ux-u-my-zero">
                        {item.title}
                    </h4>
                    <p class="ux-text-tiny ux-color-disabled ux-u-mt-zero ux-u-mb-xxxs">
                        {t(
                            `${this.translationsNamespace}:content.format.values.${item.format}`
                        ) || item.format}{" "}
                        |{" "}
                        {t(
                            `${this.translationsNamespace}:content.level.values.${item.level}`
                        ) || item.level}{" "}
                        |{" "}
                        {this.translateEventValue(item.languages, "languages")}
                    </p>
                    <p class="ux-text-normal ux-u-mt-zero ux-u-mb-xxs">
                        {item.description}
                    </p>
                    <div class="ux-u-background-grey-table-bg ux-u-px-xs ux-u-pt-xxxs ux-u-pb-xs">
                        {events.length > 1 && (
                            <ux-select
                                onUxSelectChange={this.handleSelectChange}
                                onChange={this.handleSelectChangeIndex}
                                class="ux-u-mt-xxs"
                                label={`${t(
                                    `${this.translationsNamespace}:content.startDate.label`
                                )} (${
                                    selectedIndex +
                                    1 +
                                    " " +
                                    t(
                                        `${this.translationsNamespace}:content.startDate.currentSelection`
                                    ) +
                                    " " +
                                    events.length
                                })`}
                            >
                                {events.map((event, index) => (
                                    <option
                                        key={index}
                                        value={JSON.stringify({
                                            startDate: event.startDate,
                                            languages: event.languages,
                                            duration: event.duration,
                                            location: event.location,
                                            availability: event.availability,
                                            detailsLink: event.detailsLink,
                                        })}
                                        class="ux-u-mr-xxs ux-u-mt-xxs"
                                    >
                                        {event.startDate}{" "}
                                        <span class="ux-text-normal">
                                            {`(${event.location})`}
                                        </span>
                                    </option>
                                ))}
                            </ux-select>
                        )}
                        <div class="ux-row">
                            {events.length > 0 &&
                                this.renderEventInfo(
                                    events[0],
                                    selectedEvent,
                                    isOnlineEvent
                                )}
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
